// features/super-admin/pages/FeatureFlagsPage.tsx
/**
 * SUPER ADMIN — PLANS & FEATURE FLAGS
 * Enterprise-grade plan management with feature toggles.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Shield, Zap, CheckCircle2, XCircle,
    Save, Building2, Store, Users, Package, ShoppingCart,
    CreditCard, BarChart3, FileText, Settings, Truck,
    Layers, Tag, Gift, RefreshCw, Plus, Pencil, X,
    Star, Award, Crown, Globe, Smartphone, Monitor,
    ClipboardList, ArrowRightLeft, History, TrendingUp,
    Calculator, MapPin, Sparkles, Warehouse,
    FlaskConical,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface Plan {
    id: string;
    name: string;
    code: string;
    description: string | null;
    price: number;
    currency: string;
    billingCycle: string;
    maxBusinesses: number;
    maxBranches: number;
    maxUsers: number;
    maxProducts: number;
    features: Record<string, boolean>;
    isActive: boolean;
    sortOrder: number;
}

// ============================================================
// FEATURE MODULES
// ============================================================

const FEATURE_MODULES: { module: string; label: string; description: string; icon: React.ElementType; category: string }[] = [
    { module: 'pos', label: 'POS Terminal', description: 'Point of sale operations', icon: ShoppingCart, category: 'Core' },
    { module: 'products', label: 'Product Catalog', description: 'Product management & pricing', icon: Package, category: 'Core' },
    { module: 'inventory', label: 'Inventory', description: 'Stock tracking & transfers', icon: Warehouse, category: 'Core' },
    { module: 'stock', label: 'Stock Levels', description: 'Real-time stock monitoring', icon: Layers, category: 'Core' },
    { module: 'sales', label: 'Sales History', description: 'Sales records & void/refund', icon: BarChart3, category: 'Core' },
    { module: 'customers', label: 'Customers', description: 'Customer management & loyalty', icon: Users, category: 'Core' },
    { module: 'staff', label: 'Staff Management', description: 'Staff accounts & roles', icon: Shield, category: 'Business' },
    { module: 'procurement', label: 'Procurement', description: 'Purchase orders & suppliers', icon: Truck, category: 'Business' },
    { module: 'transfers', label: 'Stock Transfers', description: 'Inter-branch transfers', icon: ArrowRightLeft, category: 'Business' },
    { module: 'reports', label: 'Reports', description: 'Business analytics & exports', icon: FileText, category: 'Business' },
    { module: 'banking', label: 'Banking', description: 'Bank accounts & transactions', icon: CreditCard, category: 'Business' },
    { module: 'expenses', label: 'Expenses', description: 'Expense tracking', icon: Tag, category: 'Business' },
    { module: 'hampers', label: 'Hampers', description: 'Gift bundles & hampers', icon: Gift, category: 'Premium' },
    { module: 'bom', label: 'BOM / Production', description: 'Bill of materials & production', icon: FlaskConical, category: 'Premium' },
    { module: 'analytics', label: 'Advanced Analytics', description: 'Movement & valuation analytics', icon: TrendingUp, category: 'Premium' },
    { module: 'locations', label: 'Warehouse Locations', description: 'Bin & location tracking', icon: MapPin, category: 'Premium' },
];

const FEATURE_CATEGORIES = ['Core', 'Business', 'Premium'];

// ============================================================
// FORMATTERS
// ============================================================

const formatNaira = (val: number) => {
    if (val === 0) return 'Free';
    if (val >= 1_000_000) return `₦${(val / 1_000_000).toFixed(1)}M`;
    return `₦${val.toLocaleString('en-NG')}`;
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function FeatureFlagsPage() {
    const navigate = useNavigate();
    const user = useAuthStore(s => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore(s => s.addToast);

    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
    const [showPlanForm, setShowPlanForm] = useState(false);
    const [planForm, setPlanForm] = useState({
        name: '', code: '', description: '', price: 0, currency: 'NGN',
        billingCycle: 'monthly', maxBusinesses: 1, maxBranches: 1,
        maxUsers: 5, maxProducts: 100, features: {} as Record<string, boolean>,
    });

    const { data: plans, isLoading } = useQuery<Plan[]>({
        queryKey: ['super-admin-plans-all'],
        queryFn: async () => {
            const { data } = await apiClient.get('/super-admin/plans');
            return data?.data || [];
        },
        enabled: !!user,
    });

    const selectedPlan = plans?.find(p => p.id === selectedPlanId);

    const savePlanMutation = useMutation({
        mutationFn: (payload: any) => editingPlan
            ? apiClient.patch(`/super-admin/plans/${editingPlan.id}`, payload)
            : apiClient.post('/super-admin/plans', payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-plans-all'] });
            addToast({ type: 'success', title: editingPlan ? 'Plan Updated' : 'Plan Created' });
            setShowPlanForm(false);
            setEditingPlan(null);
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.message }),
    });

    const togglePlanMutation = useMutation({
        mutationFn: ({ planId, isActive }: { planId: string; isActive: boolean }) =>
            apiClient.patch(`/super-admin/plans/${planId}`, { isActive }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['super-admin-plans-all'] });
            addToast({ type: 'success', title: 'Plan Updated' });
        },
    });

    const handleEditPlan = (plan: Plan) => {
        setEditingPlan(plan);
        setPlanForm({
            name: plan.name, code: plan.code, description: plan.description || '',
            price: plan.price, currency: plan.currency, billingCycle: plan.billingCycle,
            maxBusinesses: plan.maxBusinesses, maxBranches: plan.maxBranches,
            maxUsers: plan.maxUsers, maxProducts: plan.maxProducts,
            features: plan.features || {},
        });
        setShowPlanForm(true);
    };

    const handleNewPlan = () => {
        setEditingPlan(null);
        setPlanForm({
            name: '', code: '', description: '', price: 0, currency: 'NGN',
            billingCycle: 'monthly', maxBusinesses: 1, maxBranches: 1,
            maxUsers: 5, maxProducts: 100, features: {},
        });
        setShowPlanForm(true);
    };

    const toggleFeature = (module: string) => {
        setPlanForm(prev => ({
            ...prev,
            features: { ...prev.features, [module]: !prev.features[module] },
        }));
    };

    const getPlanGradient = (code: string) => {
        switch (code) {
            case 'enterprise': return 'from-amber-500 to-orange-600';
            case 'business': return 'from-blue-500 to-indigo-600';
            case 'starter': return 'from-emerald-500 to-teal-600';
            case 'free': return 'from-slate-400 to-slate-500';
            default: return 'from-violet-500 to-purple-600';
        }
    };

    const getPlanIcon = (code: string) => {
        switch (code) {
            case 'enterprise': return Crown;
            case 'business': return Building2;
            case 'starter': return Zap;
            case 'free': return Star;
            default: return Sparkles;
        }
    };

    if (isLoading) return (
        <div className="flex items-center justify-center min-h-[70vh]">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-16 h-16 rounded-2xl border-4 border-violet-200 border-t-violet-600" />
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
            <div className="max-w-[1800px] mx-auto p-4 lg:p-6 space-y-5">

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <button onClick={() => navigate('/super-admin')}
                        className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-violet-600 mb-3 group">
                        <ArrowLeft size={16} className="group-hover:-translate-x-1" /> Back to Dashboard
                    </button>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
                                <Layers size={20} className="text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Plans & Feature Flags</h1>
                                <p className="text-slate-500 text-sm mt-0.5">Manage subscription plans and feature access for tenants</p>
                            </div>
                        </div>
                        <button onClick={handleNewPlan}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-xl font-semibold shadow-lg text-sm">
                            <Plus size={16} /> Create Plan
                        </button>
                    </div>
                </motion.div>

                {/* Plans Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(plans || []).map((plan, i) => {
                        const PlanIcon = getPlanIcon(plan.code);
                        const gradient = getPlanGradient(plan.code);
                        const enabledFeatures = Object.values(plan.features || {}).filter(Boolean).length;
                        const totalFeatures = FEATURE_MODULES.length;

                        return (
                            <motion.div key={plan.id}
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                                whileHover={{ y: -6 }}
                                onClick={() => setSelectedPlanId(selectedPlanId === plan.id ? '' : plan.id)}
                                className={clsx(
                                    'relative bg-white rounded-2xl border-2 shadow-sm transition-all duration-300 cursor-pointer overflow-hidden',
                                    selectedPlanId === plan.id
                                        ? 'border-violet-400 shadow-xl ring-2 ring-violet-500/20'
                                        : 'border-slate-200 hover:border-violet-300 hover:shadow-lg'
                                )}>
                                {/* Top accent bar */}
                                <div className={`h-1.5 bg-gradient-to-r ${gradient}`} />

                                <div className="p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}>
                                            <PlanIcon size={22} className="text-white" />
                                        </div>
                                        <Badge variant={plan.isActive ? 'success' : 'danger'} size="sm">
                                            {plan.isActive ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </div>

                                    <h3 className="font-bold text-lg text-slate-900">{plan.name}</h3>
                                    <code className="text-xs text-slate-400 font-mono">{plan.code}</code>

                                    <p className="text-3xl font-black text-violet-600 mt-3">
                                        {plan.price > 0 ? `₦${plan.price.toLocaleString()}` : 'Free'}
                                        <span className="text-sm font-normal text-slate-400">/{plan.billingCycle}</span>
                                    </p>

                                    {plan.description && (
                                        <p className="text-xs text-slate-500 mt-2 line-clamp-2">{plan.description}</p>
                                    )}

                                    {/* Limits */}
                                    <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-slate-100">
                                        <div className="text-center p-2 bg-slate-50 rounded-lg">
                                            <p className="text-[10px] text-slate-400 uppercase">Businesses</p>
                                            <p className="text-sm font-bold text-slate-700">{plan.maxBusinesses}</p>
                                        </div>
                                        <div className="text-center p-2 bg-slate-50 rounded-lg">
                                            <p className="text-[10px] text-slate-400 uppercase">Branches</p>
                                            <p className="text-sm font-bold text-slate-700">{plan.maxBranches}</p>
                                        </div>
                                        <div className="text-center p-2 bg-slate-50 rounded-lg">
                                            <p className="text-[10px] text-slate-400 uppercase">Users</p>
                                            <p className="text-sm font-bold text-slate-700">{plan.maxUsers}</p>
                                        </div>
                                        <div className="text-center p-2 bg-slate-50 rounded-lg">
                                            <p className="text-[10px] text-slate-400 uppercase">Products</p>
                                            <p className="text-sm font-bold text-slate-700">{plan.maxProducts}</p>
                                        </div>
                                    </div>

                                    {/* Feature count */}
                                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                        <span className="text-xs text-slate-400">
                                            {enabledFeatures}/{totalFeatures} features
                                        </span>
                                        <div className="flex gap-1.5">
                                            <button onClick={e => { e.stopPropagation(); handleEditPlan(plan); }}
                                                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-600 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors">
                                                <Pencil size={12} /> Edit
                                            </button>
                                            <button onClick={e => {
                                                e.stopPropagation();
                                                togglePlanMutation.mutate({ planId: plan.id, isActive: !plan.isActive });
                                            }}
                                                className={clsx(
                                                    'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                                                    plan.isActive
                                                        ? 'text-rose-600 bg-rose-50 border-rose-200 hover:bg-rose-100'
                                                        : 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                                                )}>
                                                {plan.isActive ? 'Disable' : 'Enable'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}

                    {/* Add New Plan Card */}
                    <motion.button
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -4 }}
                        onClick={handleNewPlan}
                        className="bg-white rounded-2xl border-2 border-dashed border-slate-300 p-5 hover:border-violet-400 hover:bg-violet-50/30 transition-all cursor-pointer flex flex-col items-center justify-center min-h-[300px] group">
                        <div className="w-14 h-14 rounded-2xl bg-violet-100 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                            <Plus size={28} className="text-violet-500" />
                        </div>
                        <h3 className="font-bold text-slate-700 group-hover:text-violet-600 transition-colors">Create New Plan</h3>
                        <p className="text-xs text-slate-400 mt-1">Add a subscription tier</p>
                    </motion.button>
                </div>

                {/* Selected Plan — Feature Details */}
                <AnimatePresence>
                    {selectedPlan && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden">
                            <Card className="border-2 border-violet-200 shadow-lg">
                                <CardHeader gradient>
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                                            <Layers size={18} className="text-violet-500" />
                                            {selectedPlan.name} — Feature Access
                                        </h3>
                                        <Badge variant="info" size="sm">
                                            {Object.values(selectedPlan.features || {}).filter(Boolean).length} enabled
                                        </Badge>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {FEATURE_CATEGORIES.map(category => {
                                        const categoryFeatures = FEATURE_MODULES.filter(f => f.category === category);
                                        if (categoryFeatures.length === 0) return null;

                                        return (
                                            <div key={category} className="mb-6 last:mb-0">
                                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                                    {category} Features
                                                </h4>
                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                    {categoryFeatures.map(feature => {
                                                        const isEnabled = selectedPlan.features?.[feature.module] !== false;
                                                        return (
                                                            <div key={feature.module}
                                                                className={clsx(
                                                                    'p-3 rounded-xl border-2 transition-all',
                                                                    isEnabled
                                                                        ? 'bg-emerald-50 border-emerald-200'
                                                                        : 'bg-slate-50 border-slate-200 opacity-60'
                                                                )}>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <feature.icon size={15} className={isEnabled ? 'text-emerald-600' : 'text-slate-400'} />
                                                                    <span className="text-xs font-bold text-slate-700">{feature.label}</span>
                                                                </div>
                                                                <p className="text-[10px] text-slate-400 mb-2">{feature.description}</p>
                                                                {isEnabled ? (
                                                                    <Badge variant="success" size="sm">
                                                                        <CheckCircle2 size={9} className="mr-1" />Enabled
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge variant="neutral" size="sm">
                                                                        <XCircle size={9} className="mr-1" />Disabled
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Plan Form Modal */}
                <AnimatePresence>
                    {showPlanForm && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                            onClick={() => setShowPlanForm(false)}>
                            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                                onClick={e => e.stopPropagation()}
                                className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                                <div className="bg-gradient-to-r from-violet-600 to-purple-700 px-6 py-4 sticky top-0 z-10 rounded-t-3xl">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-lg font-bold text-white">
                                            {editingPlan ? `Edit ${editingPlan.name}` : 'Create New Plan'}
                                        </h2>
                                        <button onClick={() => setShowPlanForm(false)}
                                            className="p-2 rounded-xl bg-white/20 text-white hover:bg-white/30"><X size={16} /></button>
                                    </div>
                                </div>

                                <div className="p-6 space-y-5">
                                    {/* Basic Info */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Plan Name *</label>
                                            <input type="text" value={planForm.name}
                                                onChange={e => setPlanForm({ ...planForm, name: e.target.value })}
                                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                                                placeholder="e.g., Enterprise" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Code *</label>
                                            <input type="text" value={planForm.code}
                                                onChange={e => setPlanForm({ ...planForm, code: e.target.value })}
                                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                                                placeholder="e.g., enterprise" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Description</label>
                                        <textarea value={planForm.description}
                                            onChange={e => setPlanForm({ ...planForm, description: e.target.value })}
                                            rows={2} className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
                                            placeholder="Brief description of this plan..." />
                                    </div>

                                    {/* Pricing */}
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Price</label>
                                            <input type="number" value={planForm.price || ''}
                                                onChange={e => setPlanForm({ ...planForm, price: parseInt(e.target.value) || 0 })}
                                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm" placeholder="0" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Currency</label>
                                            <input type="text" value={planForm.currency}
                                                onChange={e => setPlanForm({ ...planForm, currency: e.target.value })}
                                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Billing</label>
                                            <select value={planForm.billingCycle}
                                                onChange={e => setPlanForm({ ...planForm, billingCycle: e.target.value })}
                                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white">
                                                <option value="monthly">Monthly</option>
                                                <option value="yearly">Yearly</option>
                                                <option value="quarterly">Quarterly</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Limits */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Resource Limits</label>
                                        <div className="grid grid-cols-4 gap-3">
                                            {[
                                                { key: 'maxBusinesses', label: 'Max Businesses' },
                                                { key: 'maxBranches', label: 'Max Branches' },
                                                { key: 'maxUsers', label: 'Max Users' },
                                                { key: 'maxProducts', label: 'Max Products' },
                                            ].map(limit => (
                                                <div key={limit.key}>
                                                    <label className="block text-[10px] text-slate-400 mb-1">{limit.label}</label>
                                                    <input type="number"
                                                        value={planForm[limit.key as keyof typeof planForm] as number}
                                                        onChange={e => setPlanForm({ ...planForm, [limit.key]: parseInt(e.target.value) || 1 })}
                                                        className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg text-sm text-center" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Features */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Features</label>
                                        <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto p-1">
                                            {FEATURE_MODULES.map(f => (
                                                <label key={f.module}
                                                    className={clsx(
                                                        'flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all',
                                                        planForm.features[f.module] !== false
                                                            ? 'bg-emerald-50 border-emerald-200'
                                                            : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                                                    )}>
                                                    <input type="checkbox"
                                                        checked={planForm.features[f.module] !== false}
                                                        onChange={() => toggleFeature(f.module)}
                                                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                                                    <f.icon size={16} className={planForm.features[f.module] !== false ? 'text-emerald-600' : 'text-slate-400'} />
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-700">{f.label}</p>
                                                        <p className="text-[10px] text-slate-400">{f.description}</p>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t sticky bottom-0 rounded-b-3xl">
                                    <button onClick={() => setShowPlanForm(false)}
                                        className="px-5 py-2.5 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-white transition">Cancel</button>
                                    <button onClick={() => savePlanMutation.mutate(planForm)}
                                        disabled={!planForm.name || !planForm.code || savePlanMutation.isPending}
                                        className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-violet-700 transition-all disabled:opacity-50 flex items-center gap-2">
                                        {savePlanMutation.isPending ? <Spinner size="xs" variant="white" /> : <Save size={16} />}
                                        {editingPlan ? 'Update Plan' : 'Create Plan'}
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}